import React from 'react';
import { useDispatch } from 'react-redux';
import { useCityInfoByLatLong } from 'component/common/TextSearch/TextSearchResult/NearMe/hooks/useCityInfoByLatLong';
import { GeolocationStatus } from 'core/utils/useGeolocation';
import NearMeTemplate from 'component/common/TextSearch/TextSearchResult/NearMe/NearMeTemplate';
import { SuggestionResult } from 'core/type/SuggestionResult.types';
import { closeOverlay, OverlayType, showOverlay } from 'store/slice/overlay';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesGeoLocationDenyModalSeenEventActivitiesFieldsV1,
    ActivitiesOverlayTextNearMeItemClickEventActivitiesFieldsV1,
    ActivitiesOverlayTextNearMeItemSeenEventActivitiesFieldsV1,
    ActivitiesGeoLocationNotFoundModalSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesGeoLocationDenyModalSeenEventActivitiesManifestV1,
    ActivitiesOverlayTextNearMeItemClickEventActivitiesManifestV1,
    ActivitiesOverlayTextNearMeItemSeenEventActivitiesManifestV1,
    ActivitiesGeoLocationNotFoundModalSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';

interface NearMeProps {
    handleSelectedResult: (selected: SuggestionResult) => void;
}

const NearMe: React.FC<NearMeProps> = (props) => {
    const { handleSelectedResult } = props;
    const { cityByRadiusResponse, fetchUserLocation, status: geolocationStatus } = useCityInfoByLatLong();
    const dispatch = useDispatch();
    const [callSuggest, setCallSuggest] = React.useState(false); // Flag to keep call suggest only at user action
    const isLocationGranted = geolocationStatus === GeolocationStatus.GRANTED;
    const isLocationDenied = geolocationStatus === GeolocationStatus.DENIED;
    const suggestion = cityByRadiusResponse?.suggestion;
    const showGeolocationDeniedOverlay = () => {
        trackElementEvent<ActivitiesGeoLocationDenyModalSeenEventActivitiesFieldsV1>(ActivitiesGeoLocationDenyModalSeenEventActivitiesManifestV1.ids, {});
        dispatch(closeOverlay(OverlayType.TextSearch));
        dispatch(showOverlay(OverlayType.GeoLocationDenied));
    };

    const showNoNearByActivitiesOverlay = () => {
        trackElementEvent<ActivitiesGeoLocationNotFoundModalSeenEventActivitiesFieldsV1>(ActivitiesGeoLocationNotFoundModalSeenEventActivitiesManifestV1.ids, {});
        dispatch(closeOverlay(OverlayType.TextSearch));
        dispatch(showOverlay(OverlayType.NoActivitiesNearby));
    };

    React.useEffect(() => {
        trackElementEvent<ActivitiesOverlayTextNearMeItemSeenEventActivitiesFieldsV1>(ActivitiesOverlayTextNearMeItemSeenEventActivitiesManifestV1.ids, {});
    }, []);

    React.useEffect(() => {
        if (cityByRadiusResponse && callSuggest && isLocationGranted) {
            // note: cityByRadiusResponse will be null during fetch
            if (!suggestion) {
                showNoNearByActivitiesOverlay();
            } else {
                handleSelectedResult(suggestion); // note: fallback logic is not implemented, if neede use cityByRadiusResponse.matchType to implement fallback logic
            }
            setCallSuggest(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callSuggest, cityByRadiusResponse, handleSelectedResult, isLocationGranted, suggestion]);

    React.useEffect(() => {
        if (isLocationDenied) {
            showGeolocationDeniedOverlay();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isLocationDenied]);

    const handleClick = () => {
        trackElementEvent<ActivitiesOverlayTextNearMeItemClickEventActivitiesFieldsV1>(ActivitiesOverlayTextNearMeItemClickEventActivitiesManifestV1.ids, {});

        if (isLocationDenied) {
            showGeolocationDeniedOverlay();
            return;
        }
        setCallSuggest(true);
        fetchUserLocation();
    };

    return <NearMeTemplate onClick={handleClick} />;
};

NearMe.displayName = 'NearMe';
export default NearMe;
